import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import HistoryIcon from "@mui/icons-material/History";
import StatusCommentHistoryModal from "./StatusCommentHistoryModal";

// Компонент "статус-коментар" для картки обджектива/КР на борді перегляду:
// іконка -> textarea + Зберегти; далі клік по тексту -> Оновити або Зберегти як новий.
const StatusComment = ({ comment, canEdit, onCreate, onUpdate, onLoadHistory, entityTitle, className = "" }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("view"); // "view" | "editing"
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [saveError, setSaveError] = useState("");
  const textareaRef = useRef(null);

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (mode === "editing") adjustHeight();
  }, [draft, mode]);

  const startEditing = (e) => {
    e.stopPropagation();
    setDraft(comment ? comment.comment : "");
    setSaveError("");
    setMode("editing");
  };

  const cancelEditing = (e) => {
    e.stopPropagation();
    setMode("view");
    setDraft("");
  };

  const handleCreate = async (e) => {
    e.stopPropagation();
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await onCreate(text);
      setMode("view");
      setDraft("");
    } catch (error) {
      setSaveError(error.message || t("status_comment.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.stopPropagation();
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await onUpdate(comment.id, text);
      setMode("view");
      setDraft("");
    } catch (error) {
      setSaveError(error.message || t("status_comment.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (e) => {
    e.stopPropagation();
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError("");
    try {
      setHistory(await onLoadHistory());
    } catch (error) {
      setHistoryError(error.message || t("status_comment.history_error"));
    } finally {
      setHistoryLoading(false);
    }
  };

  if (mode === "editing") {
    return (
      <div className={`status-comment status-comment-editing ${className}`} onClick={(e) => e.stopPropagation()}>
        <textarea
          ref={textareaRef}
          className="status-comment-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("status_comment.placeholder")}
          autoFocus
          rows="1"
        />
        <div className="status-comment-actions">
          <button
            type="button"
            className="status-comment-btn status-comment-btn-cancel"
            onClick={cancelEditing}
            disabled={saving}
          >
            {t("status_comment.cancel")}
          </button>
          {comment ? (
            <>
              <button
                type="button"
                className="status-comment-btn"
                onClick={handleCreate}
                disabled={saving || !draft.trim()}
              >
                {t("status_comment.save_as_new")}
              </button>
              <button
                type="button"
                className="status-comment-btn"
                onClick={handleUpdate}
                disabled={saving || !draft.trim()}
              >
                {t("status_comment.save")}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="status-comment-btn"
              onClick={handleCreate}
              disabled={saving || !draft.trim()}
            >
              {t("status_comment.save")}
            </button>
          )}
        </div>
        {saveError && <p className="status-comment-save-error" role="alert">{saveError}</p>}
      </div>
    );
  }

  if (comment) {
    return (
      <div
        className={`status-comment status-comment-text ${canEdit ? "editable" : ""} ${className}`}
        onClick={canEdit ? startEditing : (e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="status-comment-history-trigger"
          onClick={openHistory}
          aria-label={t("status_comment.open_history")}
          title={t("status_comment.open_history")}
        >
          <ChatBubbleOutlineIcon className="status-comment-current-icon" fontSize="small" />
          <HistoryIcon className="status-comment-hover-icon" fontSize="small" />
        </button>
        <span>{comment.comment}</span>
        <StatusCommentHistoryModal
          open={historyOpen}
          onClose={(e) => {
            if (e) e.stopPropagation();
            setHistoryOpen(false);
          }}
          title={entityTitle}
          comments={history}
          loading={historyLoading}
          error={historyError}
        />
      </div>
    );
  }

  if (!canEdit) return null;

  return (
    <button
      type="button"
      className={`status-comment-icon-btn ${className}`}
      onClick={startEditing}
      aria-label={t("status_comment.add")}
      title={t("status_comment.add")}
    >
      <ChatBubbleOutlineIcon fontSize="small" />
    </button>
  );
};

export default StatusComment;
